/**
 * Editor Panel — thin layout wrapper composing InputController + OutputController.
 *
 * Owns only the drag-to-resize split pane logic. All input handling, output
 * display, type detection, and visualizer coordination are delegated to the
 * child controllers and VisualizerManager hook.
 *
 * Convention: Feature components connect to stores directly via Zustand selectors.
 * Layout components (DrawerLayout, AppShell) receive content via props/slots and do NOT read application stores.
 */

import React, { useCallback, useEffect,useRef } from 'react';

import { useLocalStorage } from '@/hooks/useLocalStorage';

import { InputController } from './InputController';
import { OutputController } from './OutputController';

export const EditorPanel: React.FC = () => {
  // Drag-to-resize split state
  const endDragRef = useRef<(() => void) | null>(null);
  useEffect(() => () => { endDragRef.current?.(); }, []);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const [inputPanePercent, setInputPanePercent] = useLocalStorage<number>(
    'app.layout.editorPanel.inputPanePercent',
    50,
  );
  const isDraggingSplitRef = useRef(false);

  const clamp = useCallback(
    (value: number, min: number, max: number) => Math.min(max, Math.max(min, value)),
    [],
  );

  const handleSplitPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      const el = splitContainerRef.current;
      if (!el) return;

      e.preventDefault();
      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
      isDraggingSplitRef.current = true;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      const updateFromClientX = (clientX: number) => {
        const rect = el.getBoundingClientRect();
        const raw = ((clientX - rect.left) / rect.width) * 100;
        setInputPanePercent(clamp(raw, 20, 80));
      };

      updateFromClientX(e.clientX);

      const onMove = (ev: PointerEvent) => {
        if (!isDraggingSplitRef.current) return;
        updateFromClientX(ev.clientX);
      };

      const endDrag = () => {
        isDraggingSplitRef.current = false;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', endDrag);
        window.removeEventListener('pointercancel', endDrag);
        endDragRef.current = null;
      };
      endDragRef.current = endDrag;

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [clamp, setInputPanePercent],
  );

  return (
    <div className="h-full flex flex-col">
      <div ref={splitContainerRef} className="flex-1 min-h-0 min-w-0 flex">
        <div className="min-w-0 min-h-0 flex flex-col" style={{ flex: `0 0 ${inputPanePercent}%` }}>
          <InputController />
        </div>
        {/* Drag-to-resize handle */}
        <div
          role="separator"
          aria-orientation="vertical"
          className="w-1 bg-border cursor-col-resize hover:bg-violet-400/30 active:bg-violet-400/50 transition-colors shrink-0"
          onPointerDown={handleSplitPointerDown}
        />
        <OutputController />
      </div>
    </div>
  );
};

